import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CoinsIcon, WarningIcon } from '@phosphor-icons/react';

import {
  formatMicroUsd,
  setAgentSessionBudget,
  useAgentSessionUsage,
  type AgentSession,
  type AgentSessionUsage,
} from '@/api-models/agent-sessions';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import classNames from '@/utils/classnames';

/**
 * Spend-against-budget for one session, as a header chip that opens the limit
 * editor.
 *
 * The chip is always visible, capped or not: a session that quietly spends money
 * with no figure on screen is how a surprise invoice happens. Once a limit is set
 * the bar fills toward it and turns amber, then red, so "about to stop" is
 * legible before the stop.
 */
export function SessionBudgetChip({ session }: { session: AgentSession }) {
  const [open, setOpen] = useState(false);
  // Spend only moves while the agent is working, so don't poll a settled session.
  const live = session.status === 'running' || session.status === 'provisioning';
  const { usage } = useAgentSessionUsage(session.id, live);

  // Fall back to the session row until the first usage fetch lands, so the chip
  // paints a real number on the first frame rather than a dash.
  const spent = usage?.spent_micro_usd ?? session.spent_micro_usd;
  const limit = usage?.limit_micro_usd ?? session.budget_limit_micro_usd;
  const exhausted = usage?.exhausted ?? Boolean(session.budget_exhausted_at);
  const pct = limit && limit > 0 ? Math.min(100, (spent / limit) * 100) : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={limit === null ? 'No budget limit — click to set one' : 'Session budget'}
        className={classNames(
          'flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs transition-colors',
          exhausted
            ? 'border-danger bg-danger-tint text-danger'
            : 'border-line bg-surface-3 text-fg-secondary hover:bg-surface-4 hover:text-fg',
        )}
        aria-label="Session budget"
      >
        {exhausted ? <WarningIcon size={13} weight="fill" /> : <CoinsIcon size={13} weight="duotone" />}
        <span className="font-medium tabular-nums">{formatMicroUsd(spent)}</span>
        {limit !== null && (
          <>
            <span className="text-fg-muted">/ {formatMicroUsd(limit)}</span>
            <span className="ml-0.5 h-1 w-8 overflow-hidden rounded-full bg-surface-4">
              <span
                className={classNames(
                  'block h-full rounded-full transition-all duration-300',
                  pct >= 100 ? 'bg-danger-solid' : pct >= 80 ? 'bg-warning' : 'bg-brand',
                )}
                style={{ width: `${pct}%` }}
              />
            </span>
          </>
        )}
      </button>
      <SessionBudgetModal
        session={session}
        usage={usage}
        spentMicroUsd={spent}
        limitMicroUsd={limit}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

interface SessionBudgetModalProps {
  session: AgentSession;
  usage: AgentSessionUsage | null;
  spentMicroUsd: number;
  limitMicroUsd: number | null;
  isOpen: boolean;
  onClose: () => void;
}

function SessionBudgetModal({
  session,
  usage,
  spentMicroUsd,
  limitMicroUsd,
  isOpen,
  onClose,
}: SessionBudgetModalProps) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Reopening should show the limit as it stands, not last time's draft.
  useEffect(() => {
    if (isOpen) setValue(limitMicroUsd === null ? '' : (limitMicroUsd / 1_000_000).toFixed(2));
  }, [isOpen, limitMicroUsd]);

  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;

  async function save(limitUsd: number | null) {
    setSaving(true);
    try {
      await setAgentSessionBudget(session.id, limitUsd);
      toast.success(limitUsd === null ? 'Budget limit removed' : `Budget limit set to $${limitUsd.toFixed(2)}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the budget limit');
    } finally {
      setSaving(false);
    }
  }

  const exhausted = usage?.exhausted ?? Boolean(session.budget_exhausted_at);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Session budget"
      description="Cap what this session may spend on model calls. Reaching the cap refuses further calls and stops the sandbox."
    >
      <div className="flex flex-col gap-5 px-6 pb-6">
        {exhausted && (
          <div className="flex items-start gap-2 rounded-lg border border-danger bg-danger-tint px-3 py-2.5 text-xs text-danger">
            <WarningIcon size={15} weight="fill" className="mt-px shrink-0" />
            <span>
              This session has reached its budget and is stopped. Raise the limit below to let it continue — its history
              and files are untouched.
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label htmlFor="session-budget-limit" className="text-xs font-medium text-fg-secondary">
            Limit (USD)
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="session-budget-limit"
              type="number"
              min="0"
              step="0.5"
              inputMode="decimal"
              placeholder="No limit"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            <Button variant="primary" size={9} disabled={!valid || saving} onClick={() => void save(parsed)}>
              Save
            </Button>
          </div>
          <p className="text-xs text-fg-tertiary">
            Spend is metered per model request, and the cost of a request is only known once it finishes — so a session
            can overshoot slightly, by whatever calls were already in flight when the limit was hit.
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-line-subtle pt-4 text-xs">
          <Row label="Spent" value={formatMicroUsd(spentMicroUsd)} />
          <Row label="Limit" value={limitMicroUsd === null ? 'No limit' : formatMicroUsd(limitMicroUsd)} />
          <Row
            label="Remaining"
            value={
              usage?.remaining_micro_usd === null || usage?.remaining_micro_usd === undefined
                ? '—'
                : formatMicroUsd(usage.remaining_micro_usd)
            }
          />
          <Row label="Model requests" value={usage ? usage.request_count.toLocaleString() : '—'} />
          <Row label="Input tokens" value={usage ? usage.prompt_tokens.toLocaleString() : '—'} />
          <Row label="Output tokens" value={usage ? usage.completion_tokens.toLocaleString() : '—'} />
          <Row label="Cached input" value={usage ? usage.cached_prompt_tokens.toLocaleString() : '—'} />
          {/* Only shown once a correction exists, so the row explains a spend
              figure that otherwise wouldn't add up from the requests above. */}
          {usage !== null && usage.correction_count > 0 && (
            <Row
              label={`Corrections (${usage.correction_count})`}
              value={`${usage.corrections_micro_usd < 0 ? '−' : '+'}${formatMicroUsd(
                Math.abs(usage.corrections_micro_usd),
              )}`}
            />
          )}
        </dl>

        {usage !== null && usage.unpriced_request_count > 0 && (
          <p className="text-xs text-warning">
            {usage.unpriced_request_count} request{usage.unpriced_request_count === 1 ? '' : 's'} finished without token
            counts from the provider (an interrupted response). They count as $0, so the spend above is a lower bound.
          </p>
        )}

        {limitMicroUsd !== null && (
          <div className="flex justify-end border-t border-line-subtle pt-4">
            <Button variant="ghost" size={8} disabled={saving} onClick={() => void save(null)}>
              Remove limit
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="m-0 font-medium tabular-nums text-fg">{value}</dd>
    </div>
  );
}
